import React, { useEffect, useState } from 'react';
import { getPerformanceResponses } from '../../../services/performanceService';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend } from 'chart.js';
import { Scatter } from 'react-chartjs-2';
import { startOfWeek, endOfWeek, subWeeks, addWeeks } from 'date-fns';

ChartJS.register(CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend);

const UserPerformance = ({ user }) => {
    const [dataPoints, setDataPoints] = useState([]);
    const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));

    useEffect(() => {
        fetchResponses();
    }, [user._id, currentWeek]);

    const fetchResponses = async () => {
        try {
            const res = await getPerformanceResponses(user._id);
            const responsesArray = res.data; // Directly use the data array

            const points = processResponsesToDataPoints(responsesArray);
            setDataPoints(points);
        } catch (error) {
            console.error('Error fetching performance responses:', error);
        }
    };

    const processResponsesToDataPoints = (responses) => {
        const points = [];
        const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });

        // First pass: find min and max hours from the data
        let minHour = 23;
        let maxHour = 0;

        responses.forEach(response => {
            if (!response.score || !response.timestamp) {
                return;
            }

            const date = new Date(response.timestamp);

            // Filter responses to only include those within the current week
            if (date >= weekStart && date <= weekEnd) {
                const day = date.getDay();
                const hour = date.getHours();

                // Only consider weekdays (Mon-Fri)
                if (day >= 1 && day <= 5) {
                    minHour = Math.min(minHour, hour);
                    maxHour = Math.max(maxHour, hour);
                }
            }
        });

        // If no data, default to 8am-5pm range
        if (minHour === 23 && maxHour === 0) {
            minHour = 8;
            maxHour = 17;
        }

        // Second pass: create data points
        responses.forEach(response => {
            if (!response.score || !response.timestamp) {
                return; // Skip if score or timestamp is missing
            }

            const date = new Date(response.timestamp);

            // Filter responses to only include those within the current week
            if (date >= weekStart && date <= weekEnd) {
                const day = date.getDay(); // 0 (Sunday) to 6 (Saturday)
                const hour = date.getHours();

                // Only include Monday (1) to Friday (5) and within the dynamic hour range
                if (day >= 1 && day <= 5 && hour >= minHour && hour <= maxHour) {
                    const dayIndex = day - 1; // Monday = 0, Friday = 4
                    const hourIndex = hour - minHour;
                    points.push({
                        x: hourIndex,
                        y: dayIndex,
                        r: 7, // Radius for the point, can be adjusted
                        score: response.score,
                        minHour: minHour,
                        maxHour: maxHour,
                    });
                }
            }
        });

        return points;
    };

    // Extract minHour and maxHour from dataPoints
    const minHour = dataPoints.length > 0 ? dataPoints[0].minHour : 8;
    const maxHour = dataPoints.length > 0 ? dataPoints[0].maxHour : 17;

    const data = {
        datasets: [
            {
                label: 'Performance Score',
                data: dataPoints,
                backgroundColor: (ctx) => {
                    const value = ctx.raw ? ctx.raw.score : 0; // Safely access score
                    return value ? `rgba(31, 109, 162, ${value / 10})` : 'rgba(255, 255, 255, 0.1)'; // Use alpha to represent intensity
                },
            },
        ],
    };

    const options = {
        scales: {
            x: {
                type: 'linear',
                position: 'bottom',
                title: {
                    display: true,
                    text: 'Time of Day',
                },
                ticks: {
                    callback: (value) => {
                        const hour = minHour + value;
                        if (hour < 0 || hour > 23) return '';
                        const ampm = hour >= 12 ? 'PM' : 'AM';
                        const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
                        return `${displayHour} ${ampm}`;
                    },
                },
            },
            y: {
                type: 'linear',
                title: {
                    display: true,
                    text: 'Day of the Week',
                },
                ticks: {
                    callback: (value) => {
                        const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
                        return dayLabels[value] || '';
                    },
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: true,
                text: 'Performance Score Heatmap',
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const score = context.raw ? context.raw.score : 0;
                        const hour = minHour + context.parsed.x;
                        const ampm = hour >= 12 ? 'PM' : 'AM';
                        const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
                        const time = `${displayHour} ${ampm}`;
                        const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
                        const day = dayLabels[context.parsed.y] || '';
                        return [
                            `${day} at ${time}`,
                            `Score: ${score}`
                        ];
                    }
                }
            },
        },
        elements: {
            point: {
                radius: (ctx) => {
                    const value = ctx.raw ? ctx.raw.score : 0; // Safely access score
                    return value ? 4 + (value / 10) * 6 : 3; // Adjust point size based on score
                },
                hoverRadius: (ctx) => {
                    const value = ctx.raw ? ctx.raw.score : 0;
                    return value ? 5 + (value / 10) * 7 : 4;
                },
            },
        },
    };

    const handlePreviousWeek = () => {
        setCurrentWeek(subWeeks(currentWeek, 1));
    };

    const handleNextWeek = () => {
        setCurrentWeek(addWeeks(currentWeek, 1));
    };

    const handleCurrentWeek = () => {
        setCurrentWeek(startOfWeek(new Date(), { weekStartsOn: 1 }));
    };

    return (
        <div>
            <h3>Performance Heatmap for {user.firstname} {user.lastname}</h3>
            <div>
                <button onClick={handlePreviousWeek}>Previous Week</button> &nbsp;
                <button onClick={handleCurrentWeek}>Current Week</button> &nbsp;
                <button onClick={handleNextWeek}>Next Week</button>
            </div>
            <Scatter data={data} options={options} width={150} height={50} />
        </div>
    );
};

export default UserPerformance;
